import { threadService, useThreads } from "@api/thread";
import { Customer } from "@api/types";
import useActiveGym from "@ui/hooks/active-gym";
import NextLink from "next/link";
import { useRouter } from "next/router";
import { ReactNode, useMemo } from "react";
import { Button, ButtonProps } from "@/components/ui/button";

export function useCustomerMessageLink(customer: Customer) {
  const { gym } = useActiveGym();
  const { data: threads } = useThreads(
    { customer: customer.id, gym: gym?.id },
    { revalidateOnFocus: false },
  );
  return useMemo(() => {
    return threads?.length ? `/messages/${threads[0].id}` : `/messages/new?contact=${customer.id}`;
  }, [threads, customer.id]);
}

type LinkProps = {
  className?: string;
  children: ReactNode;
  customer: Customer;
};

export function CustomerMessageLink({ className, children, customer }: LinkProps) {
  const { gym } = useActiveGym();
  const { data: threads } = useThreads({ customer: customer.id, gym: gym?.id });
  const href = threads?.length
    ? `/messages/${threads[0].id}`
    : `/messages/new?contact=${customer.id}`;
  return (
    <NextLink href={href} className={className}>
      {children}
    </NextLink>
  );
}

interface Props extends ButtonProps {
  customer: Customer;
  children: ReactNode;
}
export function CustomerMessageButton({ children, customer, ...rest }: Props) {
  const router = useRouter();
  const { gym } = useActiveGym();
  async function handleClick() {
    const threads = await threadService.list({
      customer: customer.id,
      gym: gym?.id,
      status: "all",
    });
    const href = threads?.length
      ? `/messages/${threads[0].id}`
      : `/messages/new?contact=${customer.id}`;
    router.push(href);
  }
  if (!customer) return null;
  return (
    <Button onClick={handleClick} {...rest}>
      {children}
    </Button>
  );
}
